import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RiArrowLeftLine,
  RiSearchLine,
  RiAddLine,
  RiCloseLine,
  RiPlaneLine,
  RiStarFill,
  RiCheckLine
} from '@remixicon/react';
import { Input } from '@components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@components/ui/Tabs';
import { Card, CardContent } from '@components/ui/Card';
import { apiClient } from '@/lib/api';
import { useSearchStore } from '@/store/searchStore';
import { useAppStore } from '@/store/appStore';
import { TrustScore } from '@/features/trust-score';
import Modal from '@components/ui/Modal';
import { Tails } from '@/features/tails';

/**
 * SearchAircraft Component
 * Allows users to search for aircraft by tail number or operator
 */
const SearchAircraft = ({ showHeader = true, onClose, addtails = false }) => {
  const navigate = useNavigate();
  const { setSearchTailNumbers } = useSearchStore();
  const { setSelectedBid } = useAppStore();
  const MAX_TAIL_FIELDS = 10;

  // Tail number search state
  const [tailFields, setTailFields] = useState([{ id: 1, value: '' }]);

  // Operator search state
  const [operatorQuery, setOperatorQuery] = useState('');
  const [operatorResults, setOperatorResults] = useState([]);
  const [operatorSearchLoading, setOperatorSearchLoading] = useState(false);
  const [favoriteOperators, setFavoriteOperators] = useState([]);
  const [favoriteOperatorsLoading, setFavoriteOperatorsLoading] = useState(true);
  const [selectedOperators, setSelectedOperators] = useState([]);

  // Airport search state
  const [airportQuery, setAirportQuery] = useState('');
  const [airportResults, setAirportResults] = useState([]);
  const [airportSearchLoading, setAirportSearchLoading] = useState(false);
  const [allAirports, setAllAirports] = useState([]);
  const [selectedAirport, setSelectedAirport] = useState(null);

  // Shared state
  const [activeTab, setActiveTab] = useState('tail-number');
  const [results, setResults] = useState([]);
  const [allSearchResults, setAllSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [tailsModalOpen, setTailsModalOpen] = useState(false);

  // Drag to scroll refs and state
  const searchResultsScrollRef = useRef(null);
  const favoriteScrollRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleMouseDown = (e, scrollRef) => {
    isDragging.current = true;
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeft.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = 'grabbing';
    scrollRef.current.style.userSelect = 'none';
  };

  const handleMouseLeave = (scrollRef) => {
    isDragging.current = false;
    scrollRef.current.style.cursor = 'grab';
    scrollRef.current.style.userSelect = '';
  };

  const handleMouseUp = (scrollRef) => {
    isDragging.current = false;
    scrollRef.current.style.cursor = 'grab';
    scrollRef.current.style.userSelect = '';
  };

  const handleMouseMove = (e, scrollRef) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5; // Scroll speed multiplier
    scrollRef.current.scrollLeft = scrollLeft.current - walk;
  };

  /**
   * Auto-filter results when tail fields change
   */
  useEffect(() => {
    if (!hasSearched || allSearchResults.length === 0 || activeTab !== 'tail-number') {
      return;
    }

    // Get valid tail numbers inline
    const valid = tailFields
      .map(f => f.value.trim())
      .filter(v => v.length > 0);
    const tailNumbers = [...new Set(valid)];
    
    if (tailNumbers.length === 0) {
      setResults([]);
      return;
    }

    // Filter allSearchResults based on current tail numbers
    const filtered = allSearchResults.filter(aircraft => 
      tailNumbers.some(tn => aircraft.tail_number.toUpperCase() === tn.toUpperCase())
    );
    
    setResults(filtered);
  }, [tailFields, allSearchResults, hasSearched, activeTab]);

  /**
   * Fetch favorite/featured operators on mount (ref guard prevents StrictMode double-fetch)
   */
  const operatorsFetchedRef = useRef(false);
  useEffect(() => {
    if (operatorsFetchedRef.current) return;
    operatorsFetchedRef.current = true;
    const fetchFavoriteOperators = async () => {
      setFavoriteOperatorsLoading(true);
      try {
        // Fetch top 5 operators ordered by rating tier (Pinnacle, Premier, Benchmark, Standard)
        const operators = await apiClient.getOperators(0, 5, { sort_by: 'rating_tiers', sort_order: 'desc' });
        setFavoriteOperators(operators);
      } catch (err) {
        console.error('Error fetching favorite operators:', err);
      } finally {
        setFavoriteOperatorsLoading(false);
      }
    };
    fetchFavoriteOperators();
  }, []);

  /**
   * Fetch all airports on mount for local filtering (ref guard prevents StrictMode double-fetch)
   */
  const airportsFetchedRef = useRef(false);
  useEffect(() => {
    if (airportsFetchedRef.current) return;
    airportsFetchedRef.current = true;
    const fetchAirports = async () => {
      try {
        const airports = await apiClient.getAirports();
        setAllAirports(airports || []);
      } catch (err) {
        console.error('Error fetching airports:', err);
      }
    };
    fetchAirports();
  }, []);

  /**
   * Filter airports locally as user types
   */
  useEffect(() => {
    if (activeTab !== 'airport' || !airportQuery.trim()) {
      setAirportResults([]);
      return;
    }

    setAirportSearchLoading(true);
    const query = airportQuery.trim().toLowerCase();
    const filtered = allAirports
      .filter(a =>
        (a.icao_code && a.icao_code.toLowerCase().includes(query)) ||
        (a.iata_code && a.iata_code.toLowerCase().includes(query)) ||
        (a.name && a.name.toLowerCase().includes(query)) ||
        (a.city && a.city.toLowerCase().includes(query))
      )
      .slice(0, 10);
    setAirportResults(filtered);
    setAirportSearchLoading(false);
  }, [airportQuery, activeTab, allAirports]);

  /**
   * Debounced operator search as user types
   */
  useEffect(() => {
    if (activeTab !== 'operator' || !operatorQuery.trim()) {
      setOperatorResults([]);
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setOperatorSearchLoading(true);
      try {
        const operators = await apiClient.getOperators(0, 20, { name: operatorQuery.trim() });
        setOperatorResults(operators);
      } catch (err) {
        console.error('Error searching operators:', err);
        setOperatorResults([]);
      } finally {
        setOperatorSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [operatorQuery, activeTab]);

  /**
   * Toggle operator selection
   */
  const handleToggleOperator = (operator) => {
    setSelectedOperators(prev => {
      const isSelected = prev.some(op => op.operator_id === operator.operator_id);
      if (isSelected) {
        return prev.filter(op => op.operator_id !== operator.operator_id);
      } else {
        return [...prev, operator];
      }
    });
  };

  /**
   * Check if an operator is selected
   */
  const isOperatorSelected = (operatorId) => {
    return selectedOperators.some(op => op.operator_id === operatorId);
  };

  /**
   * Handle tab change and reset results
   */
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setResults([]);
    setError('');
    setHasSearched(false);
    // Reset input fields
    setTailFields([{ id: 1, value: '' }]);
    setOperatorQuery('');
    setSelectedOperators([]);
    setAirportQuery('');
    setAirportResults([]);
    setSelectedAirport(null);
  };

  /**
   * Add a new tail number field
   */
  const handleAddTailField = () => {
    if (tailFields.length < MAX_TAIL_FIELDS) {
      const newId = Math.max(...tailFields.map(f => f.id)) + 1;
      setTailFields([...tailFields, { id: newId, value: '' }]);
    }
  };

  /**
   * Remove a tail number field
   */
  const handleRemoveTailField = (fieldId) => {
    if (tailFields.length > 1) {
      setTailFields(tailFields.filter(f => f.id !== fieldId));
    }
  };

  /**
   * Update tail number field value
   */
  const handleTailFieldChange = (fieldId, value) => {
    setTailFields(tailFields.map(f =>
      f.id === fieldId ? { ...f, value: value.toUpperCase() } : f
    ));
  };

  /**
   * Get unique valid tail numbers
   */
  const getValidTailNumbers = useCallback(() => {
    const valid = tailFields
      .map(f => f.value.trim())
      .filter(v => v.length > 0);
    return [...new Set(valid)];
  }, [tailFields]);

  /**
   * Search by tail numbers
   */
  const handleTailSearch = async () => {
    const tailNumbers = getValidTailNumbers();

    if (tailNumbers.length === 0) {
      setError('Please enter at least one tail number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Convert tail numbers to comma-separated string
      const tailNumberString = tailNumbers.join(',');

      // Make single API call with comma-separated tail numbers (with 30s timeout)
      const searchResults = await Promise.race([
        apiClient.searchAircraftByTail(tailNumberString),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), 30000))
      ]);
      const flatResults = Array.isArray(searchResults) ? searchResults : (searchResults ? [searchResults] : []);

      // Remove duplicates by tail_number
      const uniqueResults = flatResults.reduce((acc, aircraft) => {
        if (!acc.find(a => a.tail_number === aircraft.tail_number)) {
          acc.push(aircraft);
        }
        return acc;
      }, []);

      // Store tail numbers in Zustand store
      setSearchTailNumbers(uniqueResults);
      setSelectedBid(null);

      if (addtails) {
        // Open tails modal instead of navigating
        setTailsModalOpen(true);
      } else {
        // Use setTimeout to ensure state update propagates before navigation
        setTimeout(() => {
          onClose?.();
          navigate('/tails');
        }, 0);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search aircraft');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Search by operator - fetches aircraft for all selected operators
   */
  const handleOperatorSearch = async () => {
    if (selectedOperators.length === 0) {
      setError('Please select at least one operator');
      return;
    }

    setLoading(true);
    setError('');
    setResults([]);
    setHasSearched(true);

    try {
      // Fetch aircraft for all selected operators with timeout
      const aircraftPromises = selectedOperators.map(operator =>
        Promise.race([
          apiClient.getAircrafts(0, 100, { operator_id: operator.operator_id })
            .then(aircraft => (aircraft || []).map(a => ({ ...a, operator }))),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), 30000))
        ]).catch(() => [])
      );

      const allAircraft = await Promise.all(aircraftPromises);
      const flatResults = allAircraft.flat();

      setSearchTailNumbers(flatResults);
      setSelectedBid(null);

      if (addtails) {
        // Open tails modal instead of navigating
        setTailsModalOpen(true);
      } else {
        // Use setTimeout to ensure state update propagates before navigation
        setTimeout(() => {
          onClose?.();
          navigate('/tails');
        }, 0);
      }

    } catch (err) {
      console.error('Search error:', err);
      setError('An error occurred while searching');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Search by airport - fetches aircraft based at selected airport
   */
  const handleAirportSearch = async () => {
    if (!selectedAirport) {
      setError('Please select an airport');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const airportCode = selectedAirport.icao_code || selectedAirport.iata_code || selectedAirport.name;
      const searchResults = await Promise.race([
        apiClient.getAircrafts(0, 100, { base_airport: airportCode }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), 30000))
      ]);

      const flatResults = Array.isArray(searchResults) ? searchResults : (searchResults ? [searchResults] : []);

      setSearchTailNumbers(flatResults);
      setSelectedBid(null);

      if (addtails) {
        setTailsModalOpen(true);
      } else {
        setTimeout(() => {
          onClose?.();
          navigate('/tails');
        }, 0);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search aircraft by airport');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseTailsModal = () => {
    setTailsModalOpen(false);
  };

  const handleCloseAllModals = () => {
    setTailsModalOpen(false);
    onClose?.();
  };

  /**
   * Handle search based on active tab
   */
  const handleSearch = () => {
    if (activeTab === 'tail-number') {
      handleTailSearch();
    } else if (activeTab === 'airport') {
      handleAirportSearch();
    } else {
      handleOperatorSearch();
    }
  };

  const validTailCount = getValidTailNumbers().length;

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  return (
    <div>
      {/* Header */}
      {showHeader && (
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-14">
              <button
                onClick={handleBack}
                className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Go back"
              >
                <RiArrowLeftLine className="w-5 h-5" />
              </button>
              <h1 className="ml-2 text-lg font-semibold text-primary-500">Search Aircraft</h1>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto">
        <Card className="overflow-hidden !border-0">
          <CardContent className="px-4 pt-0 pb-5">
            {/* Description */}
            <p className="text-sm text-gray-500 mb-4">
              Search by tail number or operator to find aircraft and create quotes for your clients.
            </p>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="w-full mb-4 bg-gray-100 p-1 rounded-lg">
                <TabsTrigger
                  value="tail-number"
                  className="flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm data-[state=inactive]:text-gray-500 data-[state=inactive]:hover:text-gray-700"
                >
                  Tail Number
                </TabsTrigger>
                <TabsTrigger
                  value="airport"
                  className="flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm data-[state=inactive]:text-gray-500 data-[state=inactive]:hover:text-gray-700"
                >
                  Airport
                </TabsTrigger>
                <TabsTrigger
                  value="operator"
                  className="flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm data-[state=inactive]:text-gray-500 data-[state=inactive]:hover:text-gray-700"
                >
                  Operator
                </TabsTrigger>
              </TabsList>

              {/* Tail Number Tab */}
              <TabsContent value="tail-number">
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">Bulk Search</p>
                  <p className="text-xs text-gray-500">Tail Number</p>

                  {tailFields.map((field, index) => (
                    <div key={field.id} className="relative flex items-center gap-2">
                      <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <RiPlaneLine className="h-4 w-4 text-gray-400" />
                        </div>
                        <Input
                          type="text"
                          value={field.value}
                          onChange={(e) => handleTailFieldChange(field.id, e.target.value)}
                          placeholder={index === 0 ? "Enter tail number (e.g., N123BA)" : "Enter tail number"}
                          className="pl-9 h-11"
                        />
                      </div>
                      {tailFields.length > 1 && (
                        <button
                          onClick={() => handleRemoveTailField(field.id)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                          aria-label="Remove field"
                        >
                          <RiCloseLine className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}

                  {tailFields.length < MAX_TAIL_FIELDS && (
                    <button
                      onClick={handleAddTailField}
                      className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium py-2"
                    >
                      <RiAddLine className="w-4 h-4" />
                      Add Another Tail Number
                    </button>
                  )}
                </div>
              </TabsContent>

              {/* Airport Tab */}
              <TabsContent value="airport">
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">Departure Airport</p>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <RiPlaneLine className="h-4 w-4 text-gray-400" />
                    </div>
                    <Input
                      type="text"
                      value={airportQuery}
                      onChange={(e) => {
                        setAirportQuery(e.target.value);
                        if (selectedAirport) setSelectedAirport(null);
                      }}
                      placeholder="Enter airport code or name"
                      className="pl-9 h-11"
                    />
                  </div>

                  {/* Airport suggestions dropdown */}
                  {airportQuery.trim() && !selectedAirport && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      {airportSearchLoading ? (
                        <div className="text-sm text-gray-500 py-3 text-center">Searching...</div>
                      ) : airportResults.length > 0 ? (
                        <ul className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                          {airportResults.map((airport, index) => {
                            const code = airport.icao_code || airport.iata_code;
                            return (
                              <li key={code || `airport-${index}`}>
                                <button
                                  onClick={() => {
                                    setSelectedAirport(airport);
                                    setAirportQuery(code ? `${code} - ${airport.name}` : airport.name);
                                    setAirportResults([]);
                                  }}
                                  className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors"
                                >
                                  {code && (
                                    <span className="text-sm font-medium text-gray-900">{code}</span>
                                  )}
                                  {airport.iata_code && airport.icao_code && (
                                    <span className="text-xs text-gray-400 ml-1">({airport.iata_code})</span>
                                  )}
                                  <span className={`text-sm text-gray-500 ${code ? 'ml-2' : ''}`}>{airport.name}</span>
                                  {airport.city && (
                                    <span className="text-xs text-gray-400 ml-1">- {airport.city}</span>
                                  )}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <div className="text-sm text-gray-500 py-3 text-center">No airports found</div>
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Operator Tab */}
              <TabsContent value="operator">
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Operator Name</p>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <RiSearchLine className="h-4 w-4 text-gray-400" />
                      </div>
                      <Input
                        type="text"
                        value={operatorQuery}
                        onChange={(e) => setOperatorQuery(e.target.value)}
                        placeholder="Enter operator name (e.g., Pinnacle Air Services)"
                        className="pl-9 h-11"
                      />
                    </div>
                  </div>

                  {/* Operator Search Results */}
                  {operatorQuery.trim() && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-3">Search Results</p>
                      {operatorSearchLoading ? (
                        <div className="text-sm text-gray-500 py-4 text-center">Searching...</div>
                      ) : operatorResults.length > 0 ? (
                        <div
                          ref={searchResultsScrollRef}
                          className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] cursor-grab"
                          onMouseDown={(e) => handleMouseDown(e, searchResultsScrollRef)}
                          onMouseLeave={() => handleMouseLeave(searchResultsScrollRef)}
                          onMouseUp={() => handleMouseUp(searchResultsScrollRef)}
                          onMouseMove={(e) => handleMouseMove(e, searchResultsScrollRef)}
                        >
                          {operatorResults.map((operator) => (
                            <button
                              key={operator.operator_id}
                              onClick={() => handleToggleOperator(operator)}
                              className={`grid flex-shrink-0 w-56 overflow-hidden border rounded-xl p-4 hover:shadow-sm transition-all text-left snap-start ${
                                isOperatorSelected(operator.operator_id)
                                  ? 'border-indigo-500 bg-indigo-50'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-gray-900 text-sm break-words">{operator.name}</h4>
                                  {(operator.faa_city || operator.faa_state) && (
                                    <p className="text-xs text-gray-500 break-words">
                                      {operator.faa_city}{operator.faa_state && `, ${operator.faa_state}`}
                                    </p>
                                  )}
                                </div>
                                {isOperatorSelected(operator.operator_id) && (
                                  <div className="w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <RiCheckLine className="w-3 h-3 text-white" />
                                  </div>
                                )}
                              </div>

                              <div className="flex items-center gap-2 mb-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">{Math.round(operator.trust_score || 0)} TrustScore</span>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {operator.argus_rating && operator.argus_rating !== 'No' && (
                                  <span className="text-[10px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">{operator.argus_rating}</span>
                                )}
                                {operator.wyvern_rating && operator.wyvern_rating !== 'No' && (
                                  <span className="text-[10px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">Wyvern</span>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 py-4 text-center">No operators found</div>
                      )}
                    </div>
                  )}

                  {/* Favorite Operators */}
                  {!operatorQuery.trim() && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-3">Favorite Operators</p>
                      {favoriteOperatorsLoading ? (
                        <div className="text-sm text-gray-500 py-4 text-center">Loading favorite operators...</div>
                      ) : favoriteOperators.length > 0 ? (
                      <div
                        ref={favoriteScrollRef}
                        className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] cursor-grab"
                        onMouseDown={(e) => handleMouseDown(e, favoriteScrollRef)}
                        onMouseLeave={() => handleMouseLeave(favoriteScrollRef)}
                        onMouseUp={() => handleMouseUp(favoriteScrollRef)}
                        onMouseMove={(e) => handleMouseMove(e, favoriteScrollRef)}
                      >
                        {favoriteOperators.map((operator, index) => (
                          <button
                            key={operator.operator_id}
                            onClick={() => handleToggleOperator(operator)}
                            className={`grid flex-shrink-0 w-56 overflow-hidden border rounded-xl p-4 hover:shadow-sm transition-all text-left snap-start items-start ${
                              isOperatorSelected(operator.operator_id)
                                ? 'border-indigo-500 bg-indigo-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-gray-900 text-sm break-words">{operator.name}</h4>
                                {(operator.faa_city || operator.faa_state) && (
                                  <p className="text-xs text-gray-500 break-words">
                                    {operator.faa_city}{operator.faa_state && `, ${operator.faa_state}`}
                                  </p>
                                )}
                              </div>
                              {isOperatorSelected(operator.operator_id) ? (
                                <div className="w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                                  <RiCheckLine className="w-3 h-3 text-white" />
                                </div>
                              ) : (<RiStarFill className="w-4 h-4 text-yellow-400 flex-shrink-0" />)}
                            </div>

                            <div className="flex items-center gap-2 mb-2">
                              <span className="inline-flex items-center"><TrustScore score={operator?.rating_tiers} numericScore={operator?.trust_score || 0} size="sm" /></span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {operator.argus_rating && operator.argus_rating !== 'No' && (
                                <span className="text-[10px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">ARGUS {operator.argus_rating}</span>
                              )}
                              {operator.wyvern_rating && operator.wyvern_rating !== 'No' && (
                                <span className="text-[10px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">Wyvern</span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                      ) : (
                        <div className="text-sm text-gray-500 py-4 text-center">No favorite operators</div>
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Search Button */}
              <button
                onClick={handleSearch}
                disabled={loading || (activeTab === 'tail-number' ? validTailCount === 0 : activeTab === 'airport' ? !selectedAirport : selectedOperators.length === 0)}
                className="w-full mt-8 h-12 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-full flex items-center justify-center gap-2 transition-colors"
              >
                <RiSearchLine className="w-5 h-5" />
                {loading ? 'Searching...' : activeTab === 'operator' && selectedOperators.length > 0
                  ? `Search Aircraft (${selectedOperators.length} operator${selectedOperators.length > 1 ? 's' : ''})`
                  : 'Search Aircraft'}
              </button>
          </CardContent>
        </Card>
      </main>

      {/* Tails Modal - shown when addtails prop is true */}
      <Modal
        isOpen={tailsModalOpen}
        onClose={handleCloseTailsModal}
        size="xl"
        title="Available Tails"
        closeOnOutsideClick={false}
      >
        <Tails addtails={true} onClose={handleCloseAllModals} />
      </Modal>
    </div>
  );
};

export default SearchAircraft;
